import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import React from "react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { type RunInfo } from "@/api/mlsolid";
import TimeAgo from "react-timeago";

interface MetricsCardProps {
  title: string;
  data?: string | number;
  isLoading: boolean;
  error: Error | null;
}

export function MetricsCard({
  title,
  data = "",
  isLoading,
  error,
}: MetricsCardProps) {
  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        {isLoading ? (
          <div>Loading...</div>
        ) : error ? (
          <div>something went wrong</div>
        ) : (
          <CardTitle className="text-1xl font-semibold tabular-nums @[250px]/card:text-3xl">
            <>{data}</>
          </CardTitle>
        )}
      </CardHeader>
    </Card>
  );
}

interface TimeAgoCardProps {
  title: string;
  data?: string;
  isLoading: boolean;
  error: Error | null;
}

export function TimeAgoCard({
  title,
  data,
  isLoading,
  error,
}: TimeAgoCardProps) {
  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        {isLoading ? (
          <div>Loading...</div>
        ) : error ? (
          <div>something went wrong</div>
        ) : (
          <CardTitle className="text-1xl font-semibold tabular-nums @[250px]/card:text-3xl">
            <TimeAgo live={false} date={data!} />
          </CardTitle>
        )}
      </CardHeader>
    </Card>
  );
}

interface ListCardProps {
  title: string;
  list: RunInfo[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function ListCard({ title, list, isLoading, error }: ListCardProps) {
  if (error) {
    toast.error("could not load metrics: " + error);
  }

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : !error && list ? (
          <div>
            <ScrollArea className="h-32 w-full rounded-md ">
              <div>
                <ul>
                  {list &&
                    list.map((item, index) => (
                      <React.Fragment key={index}>
                        <div className="text-sm flex justify-between">
                          <div>
                            <span
                              style={{
                                display: "inline-block",
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: item.color,
                              }}
                              className="flex justify-between"
                            />
                            {" " + item.runId}
                          </div>
                          <TimeAgo live={false} date={item.createdAt} />
                        </div>
                        <Separator className="my-2" />
                      </React.Fragment>
                    ))}
                </ul>
              </div>
            </ScrollArea>
          </div>
        ) : (
          <></>
        )}
      </CardContent>
    </Card>
  );
}

interface StringListCardProps {
  title: string;
  list: string[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function StringListCard({
  title,
  list,
  isLoading,
  error,
}: StringListCardProps) {
  if (error) {
    toast.error("could not load artifacts: " + error);
  }

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : !error && list ? (
          <ScrollArea className="h-32 w-full rounded-md">
            <div>
              <ul>
                {list.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    No artifacts
                  </div>
                )}
                {list.map((item, index) => (
                  <React.Fragment key={index}>
                    <div className="text-sm truncate">{item}</div>
                    <Separator className="my-2" />
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </ScrollArea>
        ) : (
          <></>
        )}
      </CardContent>
    </Card>
  );
}
